import { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { Mail, Loader2, Eye } from "lucide-react";
import { getPreferences, updatePreferences, previewWeeklyDigest } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

export const PreferencesCard = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [optedIn, setOptedIn] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    getPreferences()
      .then((p) => setOptedIn(!!p.email_weekly_digest))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const toggle = async () => {
    if (saving) return;
    setSaving(true);
    const next = !optedIn;
    setOptedIn(next);
    try {
      await updatePreferences({ email_weekly_digest: next });
      toast.success(next ? "You'll get a Friday recap 📬" : "Weekly recap turned off");
    } catch {
      setOptedIn(!next);
      toast.error("Couldn't save — try again");
    } finally {
      setSaving(false);
    }
  };

  const showPreview = async () => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      const p = await previewWeeklyDigest();
      setPreview(p);
    } catch {
      setPreview({ empty: true, reason: "Couldn't build a preview right now." });
    } finally {
      setPreviewLoading(false);
    }
  };

  // Sanitize preview.html with DOMPurify to block <script>/inline event handlers
  // even though the HTML comes from our own backend template. Better safe than
  // sorry — this file was flagged for XSS in the pre-deploy code review.
  const sanitizedPreviewHtml = useMemo(
    () => (preview?.html ? DOMPurify.sanitize(preview.html, {
      USE_PROFILES: { html: true },
      FORBID_TAGS: ["style", "script", "iframe", "object", "embed"],
      FORBID_ATTR: ["style", "onerror", "onload", "onclick"],
    }) : ""),
    [preview?.html]
  );

  if (loading) return null;

  return (
    <div data-testid="preferences-card" className="rounded-2xl border border-border bg-card p-6">
      <div className="flex items-start gap-3">
        <span className="grid place-items-center h-10 w-10 rounded-xl bg-accent/15 text-accent shrink-0">
          <Mail className="h-5 w-5" />
        </span>
        <div className="flex-1">
          <h3 className="font-serif text-xl font-bold">Weekly recap email</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Every Friday we'll email you your streak, quiz scores, and the concepts to shore up.
          </p>
        </div>
      </div>

      <div className="mt-5 flex items-center justify-between rounded-xl bg-secondary/50 px-4 py-3">
        <span className="text-sm font-semibold">Send me the Friday recap</span>
        <button
          data-testid="preferences-weekly-toggle"
          role="switch"
          aria-checked={optedIn}
          onClick={toggle}
          className={`relative h-6 w-11 rounded-full transition ${optedIn ? "bg-accent" : "bg-muted"}`}
        >
          <span className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white transition-transform ${optedIn ? "translate-x-5" : ""}`} />
        </button>
      </div>

      <button
        data-testid="preferences-preview-btn"
        onClick={showPreview}
        className="mt-3 w-full flex items-center justify-center gap-2 rounded-xl border-2 border-border py-2.5 text-sm font-semibold hover:bg-secondary/60 transition"
      >
        <Eye className="h-4 w-4" /> Preview my next email
      </button>

      <Dialog open={previewOpen} onOpenChange={(o) => !o && setPreviewOpen(false)}>
        <DialogContent className="max-w-lg p-0 overflow-hidden max-h-[85vh] overflow-y-auto">
          <DialogTitle className="sr-only">Weekly recap preview</DialogTitle>
          <DialogDescription className="sr-only">See exactly what your weekly Friday recap email would look like based on this week's activity.</DialogDescription>
          <div className="p-6 pb-3">
            <h2 className="font-serif text-2xl font-bold">Preview: your Friday recap</h2>
            <p className="mt-1 text-sm text-muted-foreground">This is what would land in your inbox right now.</p>
          </div>
          <div className="mx-6 mb-6 rounded-xl border border-border bg-secondary/30 overflow-hidden">
            {previewLoading ? (
              <div className="p-12 grid place-items-center">
                <Loader2 className="h-6 w-6 animate-spin text-accent" />
              </div>
            ) : preview?.empty ? (
              <div className="p-8 text-center text-sm text-muted-foreground">{preview?.reason}</div>
            ) : (
              <>
                <div className="px-5 py-3 border-b border-border text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">
                  Subject: <span className="normal-case tracking-normal text-foreground">{preview?.subject}</span>
                </div>
                <div
                  className="p-4 max-h-[50vh] overflow-y-auto"
                  data-testid="preferences-preview-html"
                  dangerouslySetInnerHTML={{ __html: sanitizedPreviewHtml }}
                />
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
